import { EN_PAGE_NAME } from '@/services/SEOService/metaData';
import { EN_REGION_PLACE_TYPE_STR } from '@yanolja-rnd/cx-joy-sdm/dist/sapporo/region/legacy/enum/EN_REGION_PLACE_TYPE';
import { observer } from 'mobx-react-lite';
import { identity, prop } from 'ramda';
import React, { useEffect, useRef } from 'react';

import NavigationBarHeading from '../../components/Common/DesignSystem/NavigationBar/Heading/NavigationBarHeading';
import NavigationBarIconButton from '../../components/Common/DesignSystem/NavigationBar/IconButton/NavigationBarIconButton';
import NavigationBar from '../../components/Common/DesignSystem/NavigationBar/NavigationBar';
import PageTitle from '../../components/Common/PageTitle/PageTitle';
import { ISunnyHeadProps } from '../../components/Common/SunnyHead';
import SunnyLayout from '../../components/Common/SunnyLayout/SunnyLayout';
import SunnyNav from '../../components/Common/SunnyNav/SunnyNav';
import { SunnyFC } from '../../components/Common/SunnyNext';
import { withSunnyPage } from '../../components/Common/SunnyPage';
import ListBody from '../../components/Leisure/CrossSell/List/Body/LeisureCrossSellListBody';
import { ILeisureCrossSellListPayload } from '../../models/Leisure/interfaces/payloads/ILeisureRegionCrossSellListPayload';
import { LeisureActions } from '../../models/Leisure/LeisureActions';
import { LeisureRequestBuilder } from '../../models/Leisure/LeisureRequestBuilder';
import { EN_PLACELIST_TYPE } from '../../models/Search/policies/ISearchListPolicy';
import { generatePageMeta } from '../../services/SEOService/metaGenerator';
import { Kinesis } from '../../services/TrackerService';
import { LeisureCrossSellListStore, LeisureCrossSellListStoreCtx } from '../../stores/LeisureCrossSellListStore';
import { forceBackFromExternal, getPageUrl, preventAndThrottle } from '../../utils';
import { getPathDivisionValue } from '../../utils/domain/place';
import { getListName } from '../../utils/domain/region';
import { useScrollStore } from '../../utils/hooks';
import { nullish } from '../../utils/yambda';
import styles from './cross-sell.module.scss';

export interface ILeisureCrossSellListProps {
  headProps: ISunnyHeadProps;
  data: ILeisureCrossSellListPayload | null;
  placeType: EN_REGION_PLACE_TYPE_STR;
  region: number;
}

const LeisureCrossSellList: SunnyFC<ILeisureCrossSellListProps> = (props) => {
  const store = useRef(new LeisureCrossSellListStore(props)).current;
  const goBack = preventAndThrottle(() => forceBackFromExternal('/'));

  const title = store.title || '이 주변 놀거리';

  useScrollStore();

  useEffect(() => {
    Kinesis.view('LeisureRecommendList', {
      listName: getListName(EN_PLACELIST_TYPE[props.placeType.toUpperCase()]),
      widgetTitle: store.title,
    });
  }, []);

  return (
    <LeisureCrossSellListStoreCtx.Provider value={store}>
      <SunnyLayout>
        {{
          header: (
            <SunnyNav
              legacyNav={
                <PageTitle className={styles.nav} left="back" onClickLeft={goBack} hasBottomBorder={true}>
                  {title}
                </PageTitle>
              }
            >
              <>
                <NavigationBar>
                  {{
                    left: <NavigationBarIconButton as="button" type="icon_arrowback" onClick={goBack} />,
                    center: <NavigationBarHeading>{title}</NavigationBarHeading>,
                  }}
                </NavigationBar>
                <NavigationBar.Shadow />
              </>
            </SunnyNav>
          ),
          body: <ListBody />,
        }}
      </SunnyLayout>
    </LeisureCrossSellListStoreCtx.Provider>
  );
};

LeisureCrossSellList.getInitialProps = async ({ req, res, query }) => {
  const headProps = generatePageMeta({
    pageUrl: getPageUrl(req),
    pageName: EN_PAGE_NAME.PAGE__LEIRURE__REGION_CROSS_SELL__LIST,
  });

  if (!query.placeType || !query.pathDivision) {
    return void res.status(404);
  }

  const pathDivisionValue = getPathDivisionValue(EN_PLACELIST_TYPE[query.placeType.toUpperCase()], query.pathDivision);
  const placeType = EN_REGION_PLACE_TYPE_STR[query.placeType];
  const region = Number(pathDivisionValue);

  const actions = new LeisureActions(LeisureRequestBuilder, req);
  const entity = actions.regionCrossSellList({
    query: {
      placeType,
      region,
      page: 1,
      limit: 20,
    },
  });

  const data = (await entity).map(prop('data')).caseOf({
    left: nullish,
    right: identity,
  });

  return {
    headProps,
    data,
    placeType,
    region,
  };
};

export default withSunnyPage()(observer(LeisureCrossSellList));
